/**
 * The selector half of the Move Refs Between Brands tool, kept pure so the
 * validation — and the dry-run gate built on it — are testable without the page.
 *
 * Everything here MIRRORS watchlock `functions/src/moveReferencesSelector.ts`.
 * A pattern the browser accepts must be one the server accepts, so patterns are
 * compiled the same way (`^(?:…)$`, no flags — case-sensitive), with the same
 * 200-character cap, and ids are counted the same way (trimmed, de-duplicated)
 * against the same 500 cap.
 */
import type {
  MoveRefsFamilyFilterField,
  MoveWatchReferencesBetweenBrandsInput,
} from '../../services/firebase';

export const MAX_REFERENCE_IDS = 500;
export const MAX_PATTERN_LENGTH = 200;
export const FAMILY_FILTER_FIELDS: readonly MoveRefsFamilyFilterField[] = [
  'series',
  'collection',
  'productName',
  'family',
];

export type SelectorMode = 'all' | 'pattern' | 'ids' | 'field';

/** The form, exactly as the curator has it. */
export interface SelectorForm {
  mode: SelectorMode;
  pattern: string;
  idsText: string;
  field: MoveRefsFamilyFilterField;
  fieldOp: 'equals' | 'matches';
  fieldValue: string;
}

export const EMPTY_SELECTOR_FORM: SelectorForm = {
  mode: 'all',
  pattern: '',
  idsText: '',
  field: 'series',
  fieldOp: 'equals',
  fieldValue: '',
};

type SelectorPayload = Pick<
  MoveWatchReferencesBetweenBrandsInput,
  'refPattern' | 'referenceIds' | 'familyFilter'
>;

/** Ids from the textarea, as the server will see them: trimmed, blank lines dropped, de-duplicated. */
export function parseReferenceIds(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\r\n]+/)
        .map((line) => line.trim())
        .filter(Boolean),
    ),
  ];
}

/** Compile as the server does, or say why not. */
export function checkPattern(pattern: string, label: string): string | null {
  if (!pattern) return `Enter a ${label}.`;
  if (pattern.length > MAX_PATTERN_LENGTH) {
    return `The ${label} must be at most ${MAX_PATTERN_LENGTH} characters (it is ${pattern.length}).`;
  }
  try {
    new RegExp(`^(?:${pattern})$`);
    return null;
  } catch (err) {
    return `Not a valid regular expression: ${err instanceof Error ? err.message : String(err)}`;
  }
}

/**
 * The selector fields for the callable, or the reason the form cannot be sent.
 *
 * `all` returns an empty payload: no selector key at all, which is the original
 * whole-brand move. A pattern is sent verbatim — not trimmed — because the
 * server does not trim it either, and a space may be part of what it matches.
 */
export function buildMoveSelectorPayload(
  form: SelectorForm,
): { payload: SelectorPayload; error: null } | { payload: null; error: string } {
  switch (form.mode) {
    case 'all':
      return { payload: {}, error: null };
    case 'pattern': {
      const error = checkPattern(form.pattern, 'pattern');
      return error ? { payload: null, error } : { payload: { refPattern: form.pattern }, error: null };
    }
    case 'ids': {
      const ids = parseReferenceIds(form.idsText);
      if (ids.length === 0) return { payload: null, error: 'Enter at least one reference id.' };
      if (ids.length > MAX_REFERENCE_IDS) {
        return {
          payload: null,
          error: `At most ${MAX_REFERENCE_IDS} ids per move (you have ${ids.length}). Split the list.`,
        };
      }
      return { payload: { referenceIds: ids }, error: null };
    }
    case 'field': {
      if (form.fieldOp === 'equals') {
        const value = form.fieldValue.trim();
        if (!value) return { payload: null, error: `Enter the ${form.field} to match.` };
        return { payload: { familyFilter: { field: form.field, equals: value } }, error: null };
      }
      const error = checkPattern(form.fieldValue, 'pattern');
      return error
        ? { payload: null, error }
        : { payload: { familyFilter: { field: form.field, matches: form.fieldValue } }, error: null };
    }
  }
}

/**
 * Identity of a move, for the dry-run gate.
 *
 * A selective wet run is allowed only after a dry run with the SAME brands and
 * the SAME effective selector. Built from the payload, not the raw form, so
 * edits that change nothing the server sees — reordering or duplicating ids,
 * switching tabs and back — do not force a second preview, while any edit that
 * would change which refs move does.
 */
export function moveFingerprint(
  sourceBrandId: string,
  targetBrandId: string,
  payload: SelectorPayload,
): string {
  const normalised: SelectorPayload = payload.referenceIds
    ? { referenceIds: [...payload.referenceIds].sort() }
    : payload;
  return JSON.stringify({ sourceBrandId, targetBrandId, ...normalised });
}

/** A wet selective move must carry a token; the server only checks it is non-empty. */
export function newConfirmationToken(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `move-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
